import {
  AlertCircle,
  CalendarDays,
  FileText,
  HelpCircle,
  Info,
  LogOut,
  MapPin,
  Mic,
  Pencil,
  Radio,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { AppShell } from "components/AppShell";
import { EventMeta } from "components/EventMeta";
import {
  PageTabPanel,
  PageTabs,
  pageTabId,
  type PageTab,
} from "components/PageTabs";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

import { AfterEventOverview } from "../after-event/AfterEventOverview";
import { useSession } from "../auth/session";
import { EventFeedbackCard } from "../feedback/EventFeedbackCard";
import { isAccepting } from "../feedback/logic";
import { useOpenFeedbackForms } from "../feedback/use-feedback";
import type { CommandState } from "../registration/use-registration-command";

import type { PublicEvent } from "../events/schemas";

import type { Membership, Signal } from "./schemas";
import { AgendaSection } from "./AgendaSection";
import { EventHero } from "./EventHero";
import { AddMeAsAttendee, canAddSelfAsAttendee } from "./AddMeAsAttendee";
import { AttendeesSection } from "./AttendeesSection";
import { SignalPickerDialog } from "./SignalPickerDialog";
import { InformationSection } from "./InformationSection";
import { PartnersSection } from "./PartnersSection";
import { MyScheduleCard } from "./MyScheduleCard";
import { ProfilePromptCard } from "./ProfilePromptCard";
import { ResourcesSection } from "./ResourcesSection";
import { SpeakersSection } from "./SpeakersSection";
import { VenuesSection } from "./VenuesSection";
import { YourProfileCard } from "./YourProfileCard";
import { YourTrackCard } from "./YourTrackCard";
import { NowSection } from "./NowSection";
import { hasHeroFields, ownStatTiles } from "./landing-model";
import { OverviewLanding } from "./OverviewLanding";
import {
  formatSessionStart,
  usePresentingSessions,
  type PresentingSessionsState,
} from "./use-presenting-sessions";
import { SignalBadges } from "./SignalBadges";
import {
  useAttendeeTrackSwitch,
  type AttendeeTrackSwitch,
} from "../track-switch/use-attendee-track-switch";
import { YourTableCard } from "../track-switch/YourTableCard";
import { DESCRIPTION_PROSE_CLASS } from "components/description-prose";
import { SEATINGS_ENABLED } from "../seatings/seatings-enabled";
import { YourSeatingCards } from "../seatings/YourSeatingCards";
import { ConnectCard } from "../connections/ConnectControls";
import { resolveSignals } from "./signal-utils";
import {
  useOwnSignalSelection,
  useSignals,
  type OwnSelectionState,
} from "./use-signals";

export function EventExperiencePage({
  event,
  membership,
  email,
  signOut,
  onWithdraw,
  withdrawState,
  onMembershipChanged,
}: {
  event: PublicEvent;
  membership: Membership;
  email: string | null;
  signOut: () => void;
  onWithdraw?: () => void;
  withdrawState?: CommandState;
  /** Read the membership again: an organiser has just added themselves. */
  onMembershipChanged?: () => void;
}) {
  const nowEnabled = event.configAgenda === true && event.configNow !== false;
  const baseTabs: PageTab<string>[] = [
    { key: "overview", label: "Overview", icon: Info },
    ...(event.configAgenda
      ? [{ key: "agenda", label: "Agenda", icon: CalendarDays }]
      : []),
    // Built from the agenda, so it follows the agenda switch as well as its
    // own. Absent means on (TNG-97).
    ...(nowEnabled ? [{ key: "now", label: "Now", icon: Radio }] : []),
    ...(event.configSpeakers
      ? [{ key: "speakers", label: "Speakers", icon: Mic }]
      : []),
    ...(event.configAttendees !== false
      ? [{ key: "attendees", label: "Attendees", icon: Users }]
      : []),
    ...(event.configPartners
      ? [{ key: "partners", label: "Partners", icon: Users }]
      : []),
    ...(event.configVenues
      ? [{ key: "venues", label: "Venues", icon: MapPin }]
      : []),
    ...(event.configResources
      ? [{ key: "resources", label: "Resources", icon: FileText }]
      : []),
    ...(event.configInformation
      ? [{ key: "info", label: "Info", icon: HelpCircle }]
      : []),
  ];
  const hasAttendeesTab = baseTabs.some((tab) => tab.key === "attendees");
  const hasAgendaTab = baseTabs.some((tab) => tab.key === "agenda");
  // Feedback is open to every member (the rules ask for a membership, not an
  // attendee row), so it counts toward the Me tab for an organiser too.
  const feedbackForms = useOpenFeedbackForms(event.eventId);
  const feedbackShown =
    feedbackForms.status === "error" ||
    (feedbackForms.status === "success" &&
      feedbackForms.data.some((form) => isAccepting(form)));

  const eventDates = useMemo(
    () => ({ startDate: event.startDate, endDate: event.endDate }),
    [event.startDate, event.endDate],
  );
  const session = useSession();
  // TNG-92: table requests, and the member's track read live while they are
  // on, so an accepted request moves the banner without a reload.
  const trackSwitch = useAttendeeTrackSwitch(event, membership);
  // ★ Signals: on first login, members with an attendee profile are prompted
  // to pick up to three from the event's pre-loaded list; the first pick is
  // the badge signal. An event with no signals never prompts — the empty list
  // is the off switch, not a config flag. Their own picks live on the Me tab,
  // so the signals are read here, ahead of the tab list.
  const signals = useSignals(event.eventId);
  const ownSignals = useOwnSignalSelection(
    event.eventId,
    membership.attendeeId,
  );
  const signalList = signals.status === "success" ? signals.data : [];
  const signalsCardShown =
    Boolean(membership.attendeeId) &&
    showsSignalsCard(signalList, ownSignals.state);
  // TNG-170: what is the member's own (feedback, their signals, their table,
  // withdrawing) lives on the Me tab, so the Overview can end at the
  // highlights. Each card keeps its own condition, and the tab is left out
  // rather than shown empty.
  const canWithdraw = Boolean(onWithdraw && membership.attendeeId);
  const meHasContent =
    feedbackShown ||
    signalsCardShown ||
    (Boolean(membership.attendeeId) &&
      (canWithdraw || trackSwitch.enabled || SEATINGS_ENABLED)) ||
    (!hasAttendeesTab && event.configConnections === true);
  const tabs: PageTab<string>[] = [
    ...baseTabs,
    ...(meHasContent ? [{ key: "me", label: "Me", icon: UserRound }] : []),
  ];
  const [activeTab, setActiveTab] = useState("overview");
  // The Me tab's "Open my schedule" opens the Agenda with that filter
  // selected. The Agenda mounts with it and owns the filter from then on; any
  // tab the person picks afterwards clears the request, so the next visit to
  // the Agenda starts on All again.
  const [agendaStartsOnSchedule, setAgendaStartsOnSchedule] = useState(false);
  const selectTab = (key: string) => {
    setAgendaStartsOnSchedule(false);
    setActiveTab(key);
  };
  const openMySchedule = () => {
    setAgendaStartsOnSchedule(true);
    setActiveTab("agenda");
    // The button that held focus leaves with the Me tab. The Agenda tab is
    // named, in the tab order, and right above the filter it opens on.
    document.getElementById(pageTabId("event-tabs", "agenda"))?.focus();
  };
  // The one-query resource listing only admins are allowed; see
  // ResourcesSection below.
  const seeAllResources =
    membership.isEventAdmin === true ||
    session?.platformRole === "admin" ||
    session?.platformRole === "super_admin";
  const displayedTab = tabs.some((tab) => tab.key === activeTab)
    ? activeTab
    : "overview";
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);

  const isWithdrawing =
    withdrawState?.status === "submitting" ||
    withdrawState?.status === "watching";
  // Read only while the dialog is open. Withdrawing removes this person from
  // every session they are down to present, and the reference app asked before
  // doing that, so the list of those sessions is what the dialog is waiting on.
  const presenting = usePresentingSessions(
    event.eventId,
    membership.attendeeId,
    showWithdrawDialog,
  );

  const [signalPicker, setSignalPicker] = useState<{
    open: boolean;
    mode: "prompt" | "edit";
  }>({ open: false, mode: "prompt" });
  const [signalsSaving, setSignalsSaving] = useState(false);
  const [prompted, setPrompted] = useState(false);

  useEffect(() => {
    if (prompted || signalList.length === 0) return;
    if (ownSignals.state.status === "not-picked") {
      setSignalPicker({ open: true, mode: "prompt" });
      setPrompted(true);
    }
  }, [prompted, signalList.length, ownSignals.state.status]);

  // The profile card waits until the signals dialog has had its turn, so the
  // two never stack: it is held back while the signals or the member's own
  // pick are loading, while the first-login prompt is about to open, and while
  // the dialog is open.
  const signalsSettling =
    signals.status === "loading" ||
    ownSignals.state.status === "loading" ||
    (ownSignals.state.status === "not-picked" &&
      signalList.length > 0 &&
      !prompted);
  const profilePromptReady = !signalPicker.open && !signalsSettling;

  const saveSignals = (signalIds: string[]) => {
    setSignalsSaving(true);
    void ownSignals.save(signalIds).then((result) => {
      setSignalsSaving(false);
      if (result === "gone") {
        // The rules refused the write for good, most likely because the
        // attendee profile was removed. Close the picker, which would otherwise
        // offer Save and Skip that both fail.
        setSignalPicker((current) => ({ ...current, open: false }));
        toast.error(
          "We could not save your signals, and trying again will not help. Your attendee profile for this event may have been removed. Ask the organisers if that is a mistake.",
        );
        return;
      }
      if (result !== "saved") {
        toast.error("Could not save your signals. Please try again.");
        return;
      }
      setSignalPicker((current) => ({ ...current, open: false }));
      toast.success(
        signalIds.length === 0
          ? "Skipped — you can pick signals any time from the Me tab."
          : "Signals saved.",
      );
    });
  };

  // QR connect (TNG-93, absent means off).
  const connectCard =
    event.configConnections === true ? (
      <ConnectCard uid={membership.uid} event={event} />
    ) : null;

  return (
    <AppShell
      identity={{ email }}
      onSignOut={signOut}
      title={event.title}
      actions={
        membership.isEventAdmin ? (
          <Badge className="rounded-full bg-hb-purple-fill text-white">
            Admin
          </Badge>
        ) : undefined
      }
    >
      <PageTabs
        tabs={tabs}
        activeTab={displayedTab}
        onTabChange={selectTab}
        idPrefix="event-tabs"
        testId="event-tabs"
      />

      <PageTabPanel
        idPrefix="event-tabs"
        tabKey="overview"
        active={displayedTab === "overview"}
      >
        {displayedTab === "overview" && (
          <OverviewSection
            event={event}
            myTrackId={trackSwitch.myTrackId}
            onOpenNow={nowEnabled ? () => setActiveTab("now") : undefined}
            uid={membership.uid}
            attendeeId={membership.attendeeId}
            isEventAdmin={membership.isEventAdmin === true}
            email={email}
            onMembershipChanged={onMembershipChanged}
            seeAllResources={seeAllResources}
            hasSignals={signalList.length > 0}
            profilePromptReady={profilePromptReady}
          />
        )}
      </PageTabPanel>
      {tabs
        .filter((tab) => tab.key !== "overview")
        .map((tab) => (
          <PageTabPanel
            key={tab.key}
            idPrefix="event-tabs"
            tabKey={tab.key}
            active={displayedTab === tab.key}
          >
            {displayedTab === "agenda" && tab.key === "agenda" && (
              <AgendaSection
                eventId={event.eventId}
                uid={membership.uid}
                timeZone={event.timezone}
                myTrackId={trackSwitch.myTrackId}
                trackSwitch={trackSwitch}
                dayTitles={event.dayTitles}
                eventDates={eventDates}
                startOnMySchedule={agendaStartsOnSchedule}
              />
            )}
            {displayedTab === "now" && tab.key === "now" && (
              <NowSection eventId={event.eventId} timeZone={event.timezone} />
            )}
            {displayedTab === "speakers" && tab.key === "speakers" && (
              <SpeakersSection
                eventId={event.eventId}
                timeZone={event.timezone}
                agendaEnabled={event.configAgenda === true}
              />
            )}
            {displayedTab === "attendees" && tab.key === "attendees" && (
              <div className="space-y-5">
                {/* TNG-170: connecting sits above the directory it is for. */}
                {connectCard}
                <AttendeesSection
                  eventId={event.eventId}
                  ownAttendeeId={membership.attendeeId}
                />
              </div>
            )}
            {displayedTab === "me" && tab.key === "me" && (
              <MeSection
                event={event}
                uid={membership.uid}
                attendeeId={membership.attendeeId}
                trackSwitch={trackSwitch}
                signalsCard={
                  <YourSignalsCard
                    signals={signalList}
                    ownSelection={ownSignals.state}
                    onEdit={() => setSignalPicker({ open: true, mode: "edit" })}
                  />
                }
                // Without the Attendees tab the connect card has no other home.
                extra={hasAttendeesTab ? undefined : connectCard}
                onOpenMySchedule={hasAgendaTab ? openMySchedule : undefined}
                onRequestWithdraw={
                  canWithdraw ? () => setShowWithdrawDialog(true) : undefined
                }
                isWithdrawing={isWithdrawing}
              />
            )}
            {displayedTab === "partners" && tab.key === "partners" && (
              <PartnersSection eventId={event.eventId} />
            )}
            {displayedTab === "venues" && tab.key === "venues" && (
              <VenuesSection eventId={event.eventId} />
            )}
            {displayedTab === "resources" && tab.key === "resources" && (
              <ResourcesSection
                eventId={event.eventId}
                uid={membership.uid}
                seeAll={seeAllResources}
              />
            )}
            {displayedTab === "info" && tab.key === "info" && (
              <InformationSection eventId={event.eventId} />
            )}
          </PageTabPanel>
        ))}

      <WithdrawDialog
        open={showWithdrawDialog}
        onOpenChange={setShowWithdrawDialog}
        eventTitle={event.title}
        timeZone={event.timezone}
        presenting={presenting}
        onConfirm={() => {
          setShowWithdrawDialog(false);
          onWithdraw?.();
        }}
        isPending={isWithdrawing}
      />

      <SignalPickerDialog
        open={signalPicker.open}
        onOpenChange={(open) =>
          setSignalPicker((current) => ({ ...current, open }))
        }
        mode={signalPicker.mode}
        signals={signalList}
        initialSignalIds={
          ownSignals.state.status === "picked" ? ownSignals.state.signalIds : []
        }
        onSave={saveSignals}
        saving={signalsSaving}
      />
    </AppShell>
  );
}

/**
 * Whether the rich-text description shows anything. The editor saves a
 * cleared field as "<p></p>", which would otherwise render an empty card.
 */
function hasVisibleDescription(html: string | undefined | null): boolean {
  if (!html) return false;
  if (/<(img|iframe|video|hr)\b/i.test(html)) return true;
  return (
    html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim().length > 0
  );
}

/**
 * Where focus goes when the profile card, which held it, is dismissed: the
 * Overview tab, which is named, already in the tab order and right above the
 * content the person was reading.
 */
function focusOverviewTab() {
  document.getElementById(pageTabId("event-tabs", "overview"))?.focus();
}

function OverviewSection({
  event,
  myTrackId,
  onOpenNow,
  uid,
  attendeeId,
  isEventAdmin,
  email,
  onMembershipChanged,
  seeAllResources,
  hasSignals,
  profilePromptReady,
}: {
  event: PublicEvent;
  myTrackId?: string;
  /** Opens the "Now" tab; absent when the event has it switched off. */
  onOpenNow?: () => void;
  uid: string;
  attendeeId?: string;
  isEventAdmin: boolean;
  email: string | null;
  onMembershipChanged?: () => void;
  seeAllResources: boolean;
  hasSignals: boolean;
  /** False while the signals dialog is open or about to open. */
  profilePromptReady: boolean;
}) {
  // No card at all without a description. An attendee only sees a published
  // event, so the old "will appear here once published" was wrong, and the
  // cards above already fill the tab.
  const description = hasVisibleDescription(event.description) ? (
    <Card data-testid="event-overview-description">
      <CardContent
        // The card's own padding is the top space. A `pt-6` here stacked on it and
        // left the text with twice as much room above as below.
        className={`pt-0 ${DESCRIPTION_PROSE_CLASS}`}
        dangerouslySetInnerHTML={{ __html: event.description ?? "" }}
      />
    </Card>
  ) : null;

  const showStatBand = hasHeroFields(event) || ownStatTiles(event).length > 0;
  // The bold order: the hero, then what the event is about, then the
  // practical cards. Without any of it the Overview is the plain one.
  const landing = showStatBand;

  return (
    <div
      className={
        landing
          ? "mx-auto w-full max-w-4xl space-y-6 pb-[env(safe-area-inset-bottom)]"
          : "space-y-6"
      }
    >
      {/* TNG-96: first on the page once the event is over, because it is
          what the page is for by then. */}
      <AfterEventOverview
        event={event}
        uid={uid}
        isEventAdmin={isEventAdmin}
        seeAllResources={seeAllResources}
        hasSignals={hasSignals}
      />
      {/* The bold hero carries the place and dates itself. */}
      {hasHeroFields(event) ? (
        <EventHero event={event} myTrackId={myTrackId} onOpenNow={onOpenNow} />
      ) : (
        <div data-testid="event-overview-meta">
          <EventMeta
            startDate={event.startDate}
            endDate={event.endDate}
            location={event.location}
          />
        </div>
      )}
      {/* Only for somebody with an attendee row: an organiser who has not
          added themselves has no directory entry to complete. */}
      {attendeeId && (
        <ProfilePromptCard
          uid={uid}
          show={profilePromptReady}
          attendeeList={event.configAttendees !== false}
          onDismissed={focusOverviewTab}
        />
      )}
      {landing && description}
      {landing && (
        <OverviewLanding event={event} showStatBand={showStatBand} uid={uid} />
      )}
      {canAddSelfAsAttendee({ isEventAdmin, attendeeId }) && (
        <AddMeAsAttendee
          eventId={event.eventId}
          uid={uid}
          email={email}
          onAdded={onMembershipChanged}
        />
      )}
      {!landing && description}
    </div>
  );
}

/**
 * The attendee's own things (TNG-170), for somebody with an attendee row: the
 * profile that the attendee list shows, their signals, their track, their
 * bookmarked sessions, the connect card when the event has no Attendees tab
 * to hold it, their table and dinner seats, the feedback forms, and the way
 * out.
 *
 * The track is named once. While theme table requests are on, YourTableCard
 * is the card that names it and shows its champions; without them,
 * YourTrackCard does.
 */
function MeSection({
  event,
  uid,
  attendeeId,
  trackSwitch,
  signalsCard,
  extra,
  onOpenMySchedule,
  onRequestWithdraw,
  isWithdrawing,
}: {
  event: PublicEvent;
  uid: string;
  attendeeId?: string;
  trackSwitch: AttendeeTrackSwitch;
  /** The member's own signals, right under the profile. */
  signalsCard?: ReactNode;
  extra?: ReactNode;
  /** Opens the Agenda on "My schedule"; absent when the event has no Agenda. */
  onOpenMySchedule?: () => void;
  onRequestWithdraw?: () => void;
  isWithdrawing: boolean;
}) {
  return (
    <div className="space-y-6" data-testid="me-content">
      {attendeeId && <YourProfileCard uid={uid} />}
      {attendeeId && signalsCard}
      {attendeeId && !trackSwitch.enabled && trackSwitch.myTrackId && (
        <YourTrackCard
          eventId={event.eventId}
          trackId={trackSwitch.myTrackId}
        />
      )}
      {attendeeId && onOpenMySchedule && (
        <MyScheduleCard
          uid={uid}
          eventId={event.eventId}
          onOpen={onOpenMySchedule}
        />
      )}
      {extra}
      {attendeeId && (
        <YourTableCard eventId={event.eventId} trackSwitch={trackSwitch} />
      )}
      {/* TNG-94: a card per published dinner seating that seats them. */}
      {attendeeId && SEATINGS_ENABLED && (
        <YourSeatingCards
          eventId={event.eventId}
          attendeeId={attendeeId}
          uid={uid}
        />
      )}
      <EventFeedbackCard eventId={event.eventId} slug={event.slug} uid={uid} />
      {onRequestWithdraw && (
        <div className="flex justify-end border-t border-border pt-4">
          <Button
            variant="destructive"
            className="min-h-11"
            onClick={onRequestWithdraw}
            disabled={isWithdrawing}
            data-testid="withdraw-button"
          >
            <LogOut className="h-4 w-4" />
            {isWithdrawing ? "Processing..." : "Withdraw from event"}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Whether the member's own signals card has anything to say: the event has
 * signals, and the member's own selection has been read and can be written
 * (an attendee profile exists). The Me tab uses it to decide it is worth
 * offering, the card to decide it renders.
 */
function showsSignalsCard(
  signals: Signal[],
  ownSelection: OwnSelectionState,
): boolean {
  return (
    signals.length > 0 &&
    (ownSelection.status === "picked" || ownSelection.status === "not-picked")
  );
}

/**
 * The member's own picks, on the Me tab, shown only when the event has signals
 * and this member can pick (has an attendee profile). Also the way back into
 * the picker after the first-login prompt was skipped or needs correcting.
 */
function YourSignalsCard({
  signals,
  ownSelection,
  onEdit,
}: {
  signals: Signal[];
  ownSelection: OwnSelectionState;
  onEdit: () => void;
}) {
  if (!showsSignalsCard(signals, ownSelection)) return null;

  const picked = resolveSignals(
    signals,
    ownSelection.status === "picked" ? ownSelection.signalIds : [],
  );

  return (
    <Card data-testid="your-signals">
      <CardContent className="flex flex-wrap items-center gap-3 py-4">
        <Sparkles className="h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground">Your signals</p>
          {picked.length > 0 ? (
            <SignalBadges signals={picked} className="mt-1.5" />
          ) : (
            <p className="text-sm text-muted-foreground">
              Pick up to three topics you want to talk about — the first goes on
              your badge.
            </p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onEdit}
          data-testid="edit-signals"
        >
          <Pencil className="h-4 w-4" />
          {picked.length > 0 ? "Edit" : "Choose signals"}
        </Button>
      </CardContent>
    </Card>
  );
}

function WithdrawDialog({
  open,
  onOpenChange,
  eventTitle,
  timeZone,
  presenting,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventTitle: string;
  timeZone?: string;
  presenting: PresentingSessionsState;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const checking = presenting.status === "loading";
  const sessions = presenting.status === "ready" ? presenting.sessions : [];
  const plural = sessions.length === 1 ? "" : "s";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Withdraw from event</DialogTitle>
          <DialogDescription>
            Are you sure you want to withdraw from {eventTitle}? Withdrawing
            ends your access to it: the event leaves your dashboard and you come
            off the attendee list. You may be able to re-register later if the
            event allows it.
          </DialogDescription>
        </DialogHeader>
        {checking && (
          <p
            className="text-sm text-muted-foreground"
            data-testid="withdraw-checking-sessions"
          >
            Checking whether you are presenting anything...
          </p>
        )}
        {sessions.length > 0 && (
          <div
            className="rounded-md border border-warning-300 bg-warning-50 p-3 text-sm text-warning-900 dark:bg-warning-950/40 dark:text-warning-100"
            data-testid="withdraw-presenter-warning"
          >
            <p className="flex items-start gap-1.5 font-medium">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              You are presenting {sessions.length} session{plural}. Withdrawing
              takes you off {sessions.length === 1 ? "it" : "them"} and tells
              the organiser the session{plural}{" "}
              {sessions.length === 1 ? "has" : "have"} lost a presenter.
              Re-registering puts you back on.
            </p>
            <ul className="mt-2 space-y-1">
              {sessions.map((session) => (
                <li key={session.id} className="flex items-start gap-1.5">
                  <Mic className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    <strong>{session.title}</strong>
                    {session.startsAt && (
                      <span className="ml-1.5 opacity-75">
                        ({formatSessionStart(session.startsAt, timeZone)})
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending || checking}
            data-testid="confirm-withdraw"
          >
            {isPending
              ? "Withdrawing..."
              : sessions.length > 0
                ? "Withdraw anyway"
                : "Withdraw"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
